import { QueryClient, UseQueryResult, useQuery } from '@tanstack/solid-query';
import type { Client } from '../generated/client/types.gen';
import type { GetAvailableRegionsResponse } from '@data-access/connect/tc-tcps';
import type { Accessor } from 'solid-js';
import {
  type ClassificationScheme,
  getClassification,
  getPointcloud,
  type GetPointcloudResponse,
  getSetting,
  listPointclouds,
  listRegions,
  listStations,
  listTilesets,
  type ListTilesetsResponse,
  type PageDataPointcloud,
  patchPointcloud,
  type PointcloudUpdateRequest,
  putSetting,
  type RegionSetting,
  removeSetting,
  type Setting,
  type Station,
  type StationTransform,
  type Tileset,
} from '../generated';
import { PointcloudStatus, TilesetFormat, TilesetStatus } from '../enums';
import { mapToPointcloudStatus } from '../point-cloud.mappers';
import { returnOrThrow, throwIfError } from '@trimble/http-utils';
import {
  getDefaultScanDataHeaders,
  ScanDataHeaders,
} from './utils/scan-data-header';
import type { Scan3dStyle } from '@trimble/types';

export type TcRegion = GetAvailableRegionsResponse;

export type PointCloudUrlId = { url: string; id: string };

const HTTP_STATUS_NOT_FOUND = 404;

const SDS_STATIONS_PAGE_SIZE = 500;

function hasProcessingPointCloud(
  page: PageDataPointcloud | undefined
): boolean {
  return (page?.items ?? []).some(
    (item) =>
      mapToPointcloudStatus(item.status ?? 'Initializing') ===
      PointcloudStatus.Processing
  );
}

export class ScanDataApiService {
  private readonly headers: ScanDataHeaders;
  private regions?: RegionSetting[];

  constructor(
    private readonly client: Client,
    clientName: string,
    clientVersion: string,
    private readonly queryClient: QueryClient
  ) {
    this.headers = getDefaultScanDataHeaders(clientName, clientVersion);
  }

  public async cancelPointCloudQueries(pointCloudId?: string): Promise<void> {
    const trackedPrefixes = ['getPointCloud', 'pointCloudUrlId'];
    // eslint-disable-next-line no-magic-numbers
    const idIndex = 2;
    await this.queryClient.cancelQueries({
      predicate: (query) =>
        trackedPrefixes.includes(query.queryKey[0] as string) &&
        (pointCloudId === undefined ||
          query.queryKey[idIndex] === pointCloudId),
    });
  }

  private async regionalOptions(region: TcRegion): Promise<{
    readonly baseUrl: string;
    readonly client: Client;
    readonly headers: ScanDataHeaders;
  }> {
    const regionSettings = await this.getRegionSettings(region);
    const url = regionSettings.sdsBaseUrl ?? '';
    const baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
    return {
      headers: this.headers,
      client: this.client,
      baseUrl,
    };
  }

  private async getRegions(): Promise<readonly RegionSetting[]> {
    if (!this.regions) {
      this.regions = await this.queryClient.fetchQuery({
        queryKey: ['regions'],
        queryFn: async () => {
          const response = await listRegions({
            headers: this.headers,
            client: this.client,
          });

          return returnOrThrow(response, 'Error while listing regions');
        },
      });
    }

    // `returnOrThrow` guarantees a populated array at runtime (it throws on a
    // missing payload); the `?? []` keeps the return typed as an array since the
    // cached `regions` field is nullable and not narrowed across the await.
    return this.regions ?? [];
  }

  public async isScanDataRegionSupported(region: TcRegion): Promise<boolean> {
    const regions = await this.getRegions();
    return regions.some((r) => r.connectRegion === region.location);
  }

  private async getRegionSettings(region: TcRegion): Promise<RegionSetting> {
    const regions = await this.getRegions();

    const regionSettings = regions.find(
      (r) => r.connectRegion === region.location
    );
    if (!regionSettings) throw new Error('Region not found');

    return regionSettings;
  }

  /**
   * `pollWhileProcessingMs` refetches the list at that interval while any
   * point cloud is still ingesting, so callers see it become ready without a
   * reload. Polling is per observer and only runs while the tab is visible.
   */
  public listPointCloudsQuery(
    region: TcRegion,
    projectId: string,
    options?: { readonly pollWhileProcessingMs?: number }
  ): UseQueryResult<PageDataPointcloud, Error> {
    const pollWhileProcessingMs = options?.pollWhileProcessingMs;
    return useQuery(() => ({
      ...(pollWhileProcessingMs !== undefined && {
        refetchInterval: (query: {
          readonly state: { readonly data?: PageDataPointcloud };
        }) =>
          hasProcessingPointCloud(query.state.data)
            ? pollWhileProcessingMs
            : false,
        refetchIntervalInBackground: false,
      }),
      queryKey: ['pointClouds', projectId, region],
      queryFn: async () => {
        const response = await listPointclouds({
          path: { projectId },
          query: { pageSize: 5000 },
          ...(await this.regionalOptions(region)),
        });

        if (
          response.error &&
          response.response?.status === HTTP_STATUS_NOT_FOUND
        ) {
          return {
            pageIndex: 0,
            pageSize: 0,
            totalItems: 0,
            items: [],
          };
        }

        return returnOrThrow(response, 'Error while listing point clouds');
      },
    }));
  }

  private async patchPointCloud(
    region: TcRegion,
    projectId: string,
    pointCloudId: string,
    body: PointcloudUpdateRequest,
    signal?: AbortSignal
  ): Promise<void> {
    const response = await patchPointcloud({
      path: { projectId, pointcloudId: pointCloudId },
      body,
      ...(await this.regionalOptions(region)),
      signal,
    });

    throwIfError(response, 'Error while patching a point cloud');

    // Only invalidate the single point-cloud query. Invalidating the
    // ['pointClouds', ...] list query would cause DataContextProvider to
    // refetch the whole list and reset every point cloud's `loadState` to
    // `NotLoaded` — a heavy side effect that, in particular, breaks the
    // active station / pane sync after a panorama rotation.
    await this.queryClient.invalidateQueries({
      queryKey: ['getPointCloud', projectId, pointCloudId, region],
    });
  }

  /**
   * Persists a new `stationTransform` for a point cloud (e.g. after a 90°
   * panorama rotation). Invalidates the corresponding `getPointCloud` query
   * so subsequent reads reflect the new transform.
   */
  public updateStationTransform(
    region: TcRegion,
    projectId: string,
    pointCloudId: string,
    stationTransform: StationTransform,
    signal?: AbortSignal
  ): Promise<void> {
    return this.patchPointCloud(
      region,
      projectId,
      pointCloudId,
      { stationTransform },
      signal
    );
  }

  public getPointCloudQuery(
    region: TcRegion,
    projectId: string,
    pointCloudId: string,
    signal: AbortSignal
  ): Promise<GetPointcloudResponse> {
    return this.queryClient.fetchQuery({
      queryKey: ['getPointCloud', projectId, pointCloudId, region],
      meta: {
        signal,
      },
      queryFn: async ({ meta, signal }) => {
        const combinedSignal = AbortSignal.any([
          signal,
          meta!['signal'] as AbortSignal,
        ]);
        const response = await getPointcloud({
          path: {
            projectId,
            pointcloudId: pointCloudId,
          },
          ...(await this.regionalOptions(region)),
          signal: combinedSignal,
        });

        return returnOrThrow(response, 'Error while getting a point cloud');
      },
    });
  }

  public getPointCloudDetailsQuery(
    region: Accessor<TcRegion | undefined>,
    projectId: Accessor<string | undefined>,
    pointCloudId: Accessor<string | undefined>
  ): UseQueryResult<GetPointcloudResponse, Error> {
    return useQuery(() => {
      const resolvedRegion = region();
      const resolvedProjectId = projectId();
      const resolvedPointCloudId = pointCloudId();
      return {
        queryKey: [
          'getPointCloud',
          resolvedProjectId,
          resolvedPointCloudId,
          resolvedRegion,
        ],
        enabled:
          resolvedRegion !== undefined &&
          Boolean(resolvedProjectId) &&
          Boolean(resolvedPointCloudId),
        queryFn: async ({ signal }) => {
          const response = await getPointcloud({
            path: {
              projectId: resolvedProjectId!,
              pointcloudId: resolvedPointCloudId!,
            },
            ...(await this.regionalOptions(resolvedRegion!)),
            signal,
          });

          return returnOrThrow(response, 'Error while getting a point cloud');
        },
      };
    });
  }

  /**
   * Fetches every page of scan stations for a point cloud (SDS legacy / non-imaging source).
   */
  public async listAllStationsQuery(
    region: TcRegion,
    projectId: string,
    pointCloudId: string,
    signal?: AbortSignal
  ): Promise<ReadonlyArray<Station>> {
    const allStations: Station[] = [];
    let pageIndex = 0;

    for (;;) {
      const response = await listStations({
        path: { projectId, pointcloudId: pointCloudId },
        query: { pageIndex, pageSize: SDS_STATIONS_PAGE_SIZE },
        ...(await this.regionalOptions(region)),
        signal,
      });

      if (
        response.error &&
        response.response?.status === HTTP_STATUS_NOT_FOUND
      ) {
        return [];
      }

      const page = returnOrThrow(response, 'Error while listing stations');
      const items = page.items ?? [];
      allStations.push(...items);

      if (
        items.length < SDS_STATIONS_PAGE_SIZE ||
        (page.totalItems !== undefined &&
          // eslint-disable-next-line sonarjs/different-types-comparison -- API can return null or omit the value at runtime despite the declared type
          page.totalItems !== null &&
          allStations.length >= page.totalItems)
      ) {
        break;
      }
      pageIndex += 1;
    }

    return allStations;
  }

  public getPointCloudUrlIdQuery(
    region: TcRegion,
    projectId: string,
    pointCloudId: string,
    signal: AbortSignal
  ): Promise<PointCloudUrlId> {
    return this.queryClient.fetchQuery({
      queryKey: ['pointCloudUrlId', projectId, pointCloudId, region],
      meta: { signal },
      queryFn: async ({
        signal: querySignal,
        meta,
      }): Promise<PointCloudUrlId> => {
        const combinedSignal = AbortSignal.any([
          querySignal,
          meta!['signal'] as AbortSignal,
        ]);
        const tilesets = await this.listTilesets(
          region,
          projectId,
          pointCloudId,
          combinedSignal
        );
        const tileSet = (tilesets as Tileset[]).find(
          (ts) =>
            (ts.status as TilesetStatus) === TilesetStatus.Ready &&
            (ts.format as TilesetFormat) === TilesetFormat.Potree
        );

        if (!tileSet) {
          throw Error('tileset not available!');
        } else if (tileSet.rootUrl == undefined) {
          throw Error('tileset rootUrl not available!');
        } else if ((tileSet.status as TilesetStatus) !== TilesetStatus.Ready) {
          throw Error('Incorrect tileset status: ' + (tileSet.status ?? ''));
        } else if ((tileSet.format as TilesetFormat) !== TilesetFormat.Potree) {
          throw Error('Incorrect tileset format: ' + (tileSet.format ?? ''));
        }

        return {
          url: tileSet.rootUrl,
          id: tileSet.id ?? '',
        };
      },
    });
  }

  private async listTilesets(
    region: TcRegion,
    projectId: string,
    pointCloudId: string,
    signal: AbortSignal
  ): Promise<ListTilesetsResponse> {
    const response = await listTilesets({
      path: {
        projectId,
        pointcloudId: pointCloudId,
      },
      ...(await this.regionalOptions(region)),
      signal,
    });

    return returnOrThrow(response, 'Error while listing tilesets');
  }

  public listClassificationSchemeQuery(
    region: TcRegion,
    projectId: string
  ): UseQueryResult<ReadonlyArray<ClassificationScheme>, Error> {
    return useQuery(() => ({
      queryKey: ['classificationScheme', projectId, region],
      queryFn: async () => {
        const response = await getClassification({
          path: { projectId },
          ...(await this.regionalOptions(region)),
        });

        return returnOrThrow(
          response,
          'Error while getting classification scheme'
        );
      },
    }));
  }

  public async getSetting(
    region: TcRegion,
    projectId: string,
    key: string
  ): Promise<Setting> {
    const response = await getSetting({
      path: { projectId, key },
      ...(await this.regionalOptions(region)),
    });

    return returnOrThrow(response, 'Error while getting setting');
  }

  public async putSetting(
    region: TcRegion,
    projectId: string,
    key: string,
    data: { [key: string]: unknown }
  ): Promise<Setting> {
    const response = await putSetting({
      path: { projectId, key },
      body: { data },
      ...(await this.regionalOptions(region)),
    });

    return returnOrThrow(response, 'Error while saving setting');
  }

  public async savePointCloudsForView(
    region: TcRegion,
    projectId: string,
    viewId: string,
    visiblePointCloudIds: string[],
    scan3dStyle?: Scan3dStyle
  ): Promise<void> {
    if (visiblePointCloudIds.length === 0) {
      return;
    }

    // Include scan3dStyle on the first scan if provided
    // This matches the format expected by handlePointClouds when loading views
    const scans = visiblePointCloudIds.map((id, index) =>
      index === 0 && scan3dStyle ? { id, scan3dStyle } : { id }
    );

    await this.putSetting(region, projectId, `view_${viewId}`, { scans });
  }

  public async deleteSetting(
    region: TcRegion,
    projectId: string,
    key: string
  ): Promise<void> {
    const response = await removeSetting({
      path: { projectId, key },
      ...(await this.regionalOptions(region)),
    });
    throwIfError(response, 'Error while deleting setting');
  }

  public async deletePointCloudAssociationsForView(
    region: TcRegion,
    projectId: string,
    viewId: string
  ): Promise<void> {
    const response = await removeSetting({
      path: { projectId, key: `view_${viewId}` },
      ...(await this.regionalOptions(region)),
    });

    if (response.error && response.response?.status === HTTP_STATUS_NOT_FOUND) {
      return;
    }

    throwIfError(response, 'Error while deleting setting');
  }
}
